import { ISelectGroup, ISelectItem } from "@components/inputs/select/Select.types";
import { TId } from "@components/table/Table.types";
import { flip, shift, useFloating } from "@floating-ui/react-dom";
import { doesElementContainsElement } from "@utils/general";
import { normalizeProp } from "@utils/zoom.utils";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { KeyName } from "../../keyName";
import { Menu } from "../inputs/select/SelectMenu";

export interface WithContextMenu {
    id?: TId;
    onContextMenu: (event: React.MouseEvent) => void;
    isContextMenuOpen: boolean;
}

export interface WithContextMenuProps {
    id?: TId;
    usePortal?: boolean;
    contextMenuItems?: ISelectItem[];
    contextMenuGroups?: ISelectGroup[];
    onContextMenuSelection?: (item: ISelectItem, id?: string) => void;
    isContextMenuDisabled?: (id?: string) => boolean;
}

export const withContextMenu = <P extends WithContextMenu>(WrappedComponent: React.ComponentType<P>) => {
    return React.forwardRef<HTMLElement, React.PropsWithChildren<Omit<P, keyof WithContextMenu>> & WithContextMenuProps>((props, ref) => {
        const { contextMenuItems, onContextMenuSelection, ...passProps } = props;

        const [pos, setPosition] = useState({ top: 0, left: 0 });
        const [isOpen, setOpen] = useState(false);
        const virtualReference = useMemo(() => ({
            getBoundingClientRect() {
                return {
                    ...pos,
                    bottom: 0, right: 0,
                    width: 0, height: 0,
                    // add some props to comply with ts interface
                    x: pos.left, y: pos.top,
                    toJSON: () => {
                    }
                };
            }
        }), [pos]);

        const { refs, floatingStyles, update, placement } = useFloating({
            placement: "bottom-start",
            middleware: [
                flip(),
                shift()
            ]
        });
        refs.setReference(virtualReference);
        // remember currentTarget, so we don't close currently opened context menu
        const currentTarget = useRef();

        const handleContextMenu = useCallback((event) => {
            event.preventDefault();
            currentTarget.current = event.target;
            setPosition({ top: normalizeProp(event.pageY), left: normalizeProp(event.pageX) });
            setOpen(true);
        }, [setPosition, setOpen]);

        const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
            if (e.key === KeyName.Escape) {
                setOpen(false);
            }
        }, [setOpen]);

        const handleSelectionChange = useCallback((item: ISelectItem) => {
            setOpen(false);
            onContextMenuSelection(item, props.id?.toString());
        }, [setOpen, onContextMenuSelection, props.id]);

        const handleMouseClick = useCallback((event: MouseEvent) => {
            const isContained = doesElementContainsElement(refs.floating.current, event.target as HTMLElement) || event.target === currentTarget.current;
            if (isContained) {
                return;
            }
            setOpen(false);
        }, [refs.floating.current, setOpen]);

        useEffect(() => {
            document.addEventListener("click", handleMouseClick);
            document.addEventListener("contextmenu", handleMouseClick);
            return () => {
                document.removeEventListener("click", handleMouseClick);
                document.removeEventListener("contextmenu", handleMouseClick);
            };
        }, [handleMouseClick]);

        let ContextMenu = (
                <Menu
                        onSelectionChange={handleSelectionChange}
                        onKeyDown={handleKeyDown}
                        value=""
                        items={contextMenuItems}
                        groups={props.contextMenuGroups}
                        isStandAlone={true}
                        popperProps={{
                            ref: refs.setFloating,
                            style: floatingStyles,
                            update,
                            placement
                        }}
                        // for some reason, in the newer versions,
                        // SimpleBar renders with zero width for the context menu
                        // => don't use it, we don't need scrollbar for context menu anyway
                        isSimpleBarDisabled
                />
        );

        const modalRoot = props.usePortal !== false && document.getElementById("context-menu-root");
        if (modalRoot) {
            ContextMenu = ReactDOM.createPortal(ContextMenu, modalRoot);
        }

        return (
                <>
                    <WrappedComponent {...passProps as P}
                                      ref={ref}
                                      onContextMenu={contextMenuItems?.length && handleContextMenu}
                                      isContextMenuOpen={isOpen}/>
                    {isOpen && !props.isContextMenuDisabled?.(props.id?.toString()) && ContextMenu}
                </>
        );
    });
};